import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { Workforce } from "@/types/workforce";

export const useWorkforceDetail = (workforceId: string | undefined) => {
	const [workforce, setWorkforce] = useState<Workforce | null>(null);
	const [loading, setLoading] = useState(true);
	const [formattedDate, setFormattedDate] = useState<string>("");

	const fetchWorkforceDetail = async () => {
		if (!workforceId) {
			setLoading(false);
			return;
		}

		try {
			setLoading(true);

			// Fetch the workforce details with related data
			const { data, error } = await supabase
				.from('workforce')
				.select(`
					*,
					candidates!inner(id, name, email, phone, function, skills),
					jobs(id, title, companies(id, name))
				`)
				.eq('id', workforceId)
				.single();

			if (error) {
				console.error("Error fetching workforce details:", error);
				setLoading(false);
				return;
			}

			// Fetch assigned recruiter profile if exists
			let recruiterProfile = null;
			if (data?.assigned_recruiter) {
				const { data: profileData } = await supabase
					.rpc('get_user_display_info', {
						p_user_id: data.assigned_recruiter
					});

				recruiterProfile = profileData && profileData.length > 0 ? profileData[0] : null;
			}

			// Format the workforce data
			const formattedWorkforce: Workforce = {
				id: data.id,
				organization_uuid: data.organization_uuid,
				candidate_id: data.candidate_id,
				candidate: data.candidates,
				job_id: data.job_id,
				job: data.jobs ? {
					id: data.jobs.id,
					title: data.jobs.title,
					company: data.jobs.companies
				} : undefined,
				external_job_title: data.external_job_title,
				external_company_name: data.external_company_name,
				external_company_contact: data.external_company_contact,
				external_job_description: data.external_job_description,
				start_date: data.start_date,
				end_date: data.end_date,
				expected_end_date: data.expected_end_date,
				status: data.status,
				status_change_reason: data.status_change_reason,
				status_change_note: data.status_change_note,
				daily_rate: data.daily_rate,
				hourly_rate: data.hourly_rate,
				currency: data.currency,
				billing_type: data.billing_type,
				purchase_price_daily: data.purchase_price_daily,
				purchase_price_hourly: data.purchase_price_hourly,
				selling_price_daily: data.selling_price_daily,
				selling_price_hourly: data.selling_price_hourly,
				location: data.location,
				location_type: data.location_type,
				work_percentage: data.work_percentage,
				contract_type: data.contract_type,
				contract_reference: data.contract_reference,
				assigned_recruiter: data.assigned_recruiter,
				assigned_recruiter_profile: recruiterProfile ? {
					id: data.assigned_recruiter,
					name: `${recruiterProfile.first_name || ''} ${recruiterProfile.last_name || ''}`.trim(),
					first_name: recruiterProfile.first_name,
					last_name: recruiterProfile.last_name
				} : undefined,
				notes: data.notes,
				custom_fields: data.custom_fields || {},
				is_bench: data.is_bench,
				bench_reason: data.bench_reason,
				bench_daily_cost: data.bench_daily_cost,
				created_at: data.created_at,
				updated_at: data.updated_at
			};

			setWorkforce(formattedWorkforce);

			// Format the date
			if (data.created_at) {
				try {
					const date = new Date(data.created_at);
					if (!isNaN(date.getTime())) {
						const relativeTime = formatDistanceToNow(date, { addSuffix: true });
						setFormattedDate(relativeTime);
					} else {
						setFormattedDate("Invalid date");
					}
				} catch (err) {
					console.error("Error formatting date:", err);
					setFormattedDate("Date error");
				}
			}

		} catch (error) {
			console.error("Error fetching workforce details:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchWorkforceDetail();
	}, [workforceId]);

	return {
		workforce,
		loading,
		formattedDate,
		refetch: fetchWorkforceDetail
	};
};
